import React from "react";
import { ShieldCheck, ClipboardCheck, GraduationCap, FileText, ArrowUpRight } from "lucide-react";
import { fmtDate } from "@/lib/dates";
import { RAG_LABEL } from "@/lib/rag";
import { NextPriorities } from "@/components/evidence/NextPriorities";

const TONE = { green: "text-rag-green", amber: "text-rag-amber", red: "text-rag-red", none: "text-brand" };
const RING = { green: "#2E9E6B", amber: "#D99A1C", red: "#C94A3C", none: "#C9C2B6" };

function Ring({ pct, tone }) {
  const value = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="relative h-16 w-16 grid place-items-center shrink-0">
      <svg className="h-16 w-16 -rotate-90" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#EFEAE2" strokeWidth="3.2" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={RING[tone]} strokeWidth="3.2" strokeDasharray={`${value}, 100`} strokeLinecap="round" />
      </svg>
      <span className={`absolute text-sm font-bold font-heading ${TONE[tone]}`}>{pct == null ? "—" : `${pct}%`}</span>
    </div>
  );
}

function Card({ testId, icon: Icon, label, headline, tone = "none", ring, lines = [], onGo, goLabel }) {
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border border-cream-deep p-5 flex flex-col gap-3 min-w-0 transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2 text-brand"><Icon className="h-4 w-4" /><span className="label-caps">{label}</span></div>
      <div className="flex items-center gap-4">
        {ring !== undefined && <Ring pct={ring} tone={tone} />}
        <div className="min-w-0">
          <div data-testid={`${testId}-headline`} className={`font-heading font-bold text-lg 2xl:text-xl leading-tight ${TONE[tone]}`}>{headline}</div>
          {lines.filter(Boolean).map((l, i) => <div key={i} className="text-xs text-muted-foreground leading-snug mt-0.5">{l}</div>)}
        </div>
      </div>
      <button type="button" onClick={onGo} data-testid={`${testId}-link`} className="mt-auto inline-flex items-center gap-1 text-xs font-semibold text-coral hover:underline self-start">{goLabel}<ArrowUpRight className="h-3 w-3" /></button>
    </div>
  );
}

const d = (iso) => (iso ? fmtDate(iso) : "—");

export function OverviewDashboard({ live, liveRag, assessed, complete = true, assurance, audited, sampleSize, monthlyDone, monthlyCount = 0, currentRound, overview, keyDocsTpl, keyDocs, training, priorities, onGo }) {
  const workingLine = !assessed ? "Working assessment not started"
    : !complete ? `Assessment in progress · score for completed checks: ${live.overall}%`
    : `Current working assessment: ${live.overall}% · ${RAG_LABEL[liveRag]}`;
  const signed = assurance?.last_score != null;
  const docsProvided = (keyDocsTpl || []).filter((k) => keyDocs?.[k.key]).length;
  const docsTotal = (keyDocsTpl || []).length;
  const docsOverdue = overview?.documents?.overdue || 0;
  const champCert = overview?.champion?.certificate_uploaded;
  const t = training || {};
  const trainingTone = t.completion_pct == null ? "none" : t.completion_pct >= 90 ? "green" : t.completion_pct >= 75 ? "amber" : "red";
  const auditPct = sampleSize ? Math.round((monthlyCount / sampleSize) * 100) : 0;
  const auditTone = !monthlyDone ? (audited ? "amber" : "none") : monthlyCount >= sampleSize ? "green" : "amber";

  return (
    <div data-testid="overview-dashboard" className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4">
        <Card testId="ov-card-assurance" icon={ShieldCheck} label="Overall assurance" tone={signed ? assurance.rag : "none"} ring={signed ? assurance.last_score : null}
          headline={signed ? assurance.status_label : "Not yet assessed"}
          lines={[signed ? `Signed off ${d(assurance.last_assessment_at)} · next due ${d(assurance.next_due_at)}` : "Complete your first quarterly review to record a signed-off score",
                  workingLine]}
          onGo={() => onGo("review", "review-top")} goLabel="Assurance review" />
        <Card testId="ov-card-audits" icon={ClipboardCheck} label="Monthly resident audits" tone={auditTone} ring={auditPct}
          headline={`${monthlyCount} / ${sampleSize} dated this month`}
          lines={[monthlyDone ? `${new Date().toLocaleString("en-GB", { month: "long" })} audit underway` : `Not started in ${new Date().toLocaleString("en-GB", { month: "long" })}`, `Round ${currentRound?.round_no || 1} · ${audited} of ${sampleSize} residents audited in this round`]}
          onGo={() => onGo("residents", "resident-audit")} goLabel="Resident audits" />
        <Card testId="ov-card-training" icon={GraduationCap} label="Staff training" tone={trainingTone} ring={t.completion_pct ?? null}
          headline={t.staff_count ? `${t.completion_pct}% complete` : "No staff added"}
          lines={[t.staff_count ? `${t.staff_count} team member${t.staff_count === 1 ? "" : "s"} · ${t.overdue || 0} overdue` : "Add your team to track training", t.certificates ? `${t.certificates} certificate${t.certificates === 1 ? "" : "s"} issued` : null]}
          onGo={() => onGo("evidence", "staff-training")} goLabel="Training records" />
        <Card testId="ov-card-evidence" icon={FileText} label="Evidence documents" tone={docsOverdue ? "red" : docsProvided === docsTotal && docsTotal ? "green" : docsProvided ? "amber" : "none"} ring={docsTotal ? Math.round(docsProvided / docsTotal * 100) : null}
          headline={`${docsProvided} / ${docsTotal} uploaded`}
          lines={[docsOverdue ? `${docsOverdue} annual review${docsOverdue === 1 ? "" : "s"} overdue` : "Key policies, assessments and audits", champCert ? "Oral Champion certificate on file" : "Oral Champion certificate not uploaded"]}
          onGo={() => onGo("evidence", "key-documents")} goLabel="Evidence & training" />
      </div>
      <NextPriorities items={priorities} assessed={assessed} onGo={onGo} />
    </div>
  );
}
